// src/store/accessibilityStore.js
// User accessibility preferences — persisted to localStorage.
//
// Five settings:
//   largeText       — scale all UI text up by 1.35×
//   highContrast    — force maximum contrast (dark bg, white text, thick borders)
//   colorblindMode  — replace color-only indicators with shape+pattern+label
//   reduceMotion    — pause all animations (also respects prefers-reduced-motion)
//   voiceAlerts     — read critical alerts aloud via speechSynthesis API
//
// Each setting applies a CSS class to <html> and/or changes how
// dynamic content is rendered.

import { create }  from 'zustand';
import { persist } from 'zustand/middleware';

export const useAccessibilityStore = create(
  persist(
    (set, get) => ({
      largeText:      false,
      highContrast:   false,
      colorblindMode: false,
      reduceMotion:   false,
      voiceAlerts:    false,
      lowPowerMode:   false,
      bandwidthMode:  false,

      toggleLargeText:      () => set(s => ({ largeText:      !s.largeText })),
      toggleHighContrast:   () => set(s => ({ highContrast:   !s.highContrast })),
      toggleColorblindMode: () => set(s => ({ colorblindMode: !s.colorblindMode })),
      toggleReduceMotion:   () => set(s => ({ reduceMotion:   !s.reduceMotion })),
      toggleVoiceAlerts:    () => set(s => ({ voiceAlerts:    !s.voiceAlerts })),
      toggleLowPowerMode:   () => set(s => ({ lowPowerMode:   !s.lowPowerMode })),
      setLowPowerMode:      (v) => set({ lowPowerMode: v }),
      toggleBandwidthMode:  () => set(s => ({ bandwidthMode: !s.bandwidthMode })),
      setBandwidthMode:     (v) => set({ bandwidthMode: v }),

      // Apply all active classes to <html> — called once on mount
      applyToDOM: () => {
        const s = get();
        const cl = document.documentElement.classList;
        cl.toggle('eos-large-text',      s.largeText);
        cl.toggle('eos-high-contrast',   s.highContrast);
        cl.toggle('eos-colorblind',      s.colorblindMode);
        cl.toggle('eos-low-power',       s.lowPowerMode);
        cl.toggle('eos-bandwidth',        s.bandwidthMode);
        cl.toggle('eos-reduce-motion',   s.reduceMotion ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        // high contrast also fires if OS requests it
        if (window.matchMedia('(prefers-contrast: more)').matches) {
          cl.add('eos-high-contrast');
        }
        if (window.matchMedia('(forced-colors: active)').matches) {
          cl.add('eos-high-contrast');
        }
      },
    }),
    { name: 'eos-accessibility-v1' }
  )
);
